// Theme state for the Light / Dark / High-Contrast theming (A-P2).
//
// The single source of truth is the `data-theme` attribute on <html>
// (see tailwind.config.ts `darkMode` + src/index.css token blocks). The
// initial value is set before first paint by the no-flash seed in
// index.html; this module keeps it in sync afterwards and lets React
// components read/set it. Persistence is localStorage now; when the
// User-Settings page (A-P3) lands, the stored preference syncs to the
// user profile and this stays the fast local cache.
//
// Keep STORAGE_KEY, the value set, and the "system" resolution in sync
// with the seed script in index.html.
//
// Surfaced in the user menu (CD-009) as Hell / Dunkel / System - the three
// LP-023 asks for. See docs/uxdd/design-theming-architecture.md.

import { useCallback, useSyncExternalStore } from "react";

/** A user-selectable theme. "system" follows the OS (colour scheme + contrast). */
export type Theme = "light" | "dark" | "high-contrast" | "system";
/** A concrete theme actually applied to the DOM (never "system"). */
export type ResolvedTheme = "light" | "dark" | "high-contrast";

/** The choices the user menu offers (LP-023). "high-contrast" waits for O-014. */
export const THEMES: readonly Theme[] = ["system", "light", "dark"] as const;

const STORAGE_KEY = "ryd-theme";
// CD-002 / LP-023: "Die Voreinstellung sollte vom System übernommen werden."
const DEFAULT_THEME: Theme = "system";

// Module-level listeners so every useTheme() caller stays in agreement
// without a Provider.
const listeners = new Set<() => void>();
function emit(): void {
  for (const listener of listeners) listener();
}

function isTheme(value: string | null): value is Theme {
  return (
    value === "light" ||
    value === "dark" ||
    value === "high-contrast" ||
    value === "system"
  );
}

function getStoredTheme(): Theme {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isTheme(value) ? value : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

// "system" follows the colour scheme only: there are no high-contrast tokens
// in index.css yet, and resolving to a theme without tokens would silently show
// light to someone whose OS is dark. High contrast returns with O-014.
function resolveSystem(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

/** Resolve a (possibly "system") theme to the concrete theme to apply. */
export function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? resolveSystem() : theme;
}

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = resolveTheme(theme);
}

/** Set + persist the theme, apply it to <html>, and notify listeners. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Private-mode / disabled storage: fall back to a session-only apply.
  }
  applyTheme(theme);
  emit();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);

  // Re-resolve when the OS changes while on "system", and when another tab
  // updates the stored preference.
  const mqScheme = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onMedia = (): void => {
    if (getStoredTheme() === "system") {
      applyTheme("system");
      callback();
    }
  };
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) {
      applyTheme(getStoredTheme());
      callback();
    }
  };
  mqScheme?.addEventListener("change", onMedia);
  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(callback);
    mqScheme?.removeEventListener("change", onMedia);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * Read and set the current theme. Returns the stored selection (`theme`,
 * which may be "system"), the concrete `resolved` theme on the DOM, and a
 * `setTheme` setter.
 */
export function useTheme(): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (theme: Theme) => void;
} {
  const theme = useSyncExternalStore(subscribe, getStoredTheme, () => DEFAULT_THEME);
  const set = useCallback((next: Theme) => setTheme(next), []);
  return { theme, resolved: resolveTheme(theme), setTheme: set };
}
